import { get, ref, set } from 'firebase/database';
import { auth, database } from '../firebase';

const BACKEND_URL =
  process.env.NODE_ENV === 'production'
    ? (
      process.env.REACT_APP_BACKEND_URL_PROD ||
      process.env.REACT_APP_BACKEND_URL ||
      process.env.REACT_APP_BACKEND_URL_PROD ||
      'http://localhost:8000'
    )
    : (
      process.env.REACT_APP_BACKEND_URL_PROD ||
      process.env.REACT_APP_BACKEND_URL ||
      process.env.REACT_APP_BACKEND_URL_PROD ||
      'http://localhost:8000'
    );

const jsonHeaders = {
  'Content-Type': 'application/json'
};
const HEADCOUNT_TARGET_SETTINGS_PATH = 'analytics_settings/headcount_targets';
const ANALYTICS_QUERY_CACHE_PREFIX = 'analytics-query-cache-v1:';
const ANALYTICS_QUERY_STALE_MS = 5 * 60 * 1000;
export const ANALYTICS_SESSION_CACHE_INVALIDATED_EVENT = 'analytics-session-cache-invalidated';
const analyticsQueryMemoryCache = new Map();
const analyticsQueryPendingRequests = new Map();
let analyticsCacheInvalidationVersion = 0;

function stableStringify(value) {
  if (value === null || value === undefined) return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map((item) => stableStringify(item)).join(',')}]`;
  if (typeof value === 'object') {
    return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`;
  }
  return JSON.stringify(value);
}

function cloneJson(value) {
  if (value === undefined) return value;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return value;
  }
}

function buildAnalyticsQueryCacheKey({
  companyUid = '',
  screenId = '',
  widgetId = '',
  filters = {},
  groupBy = '',
  revision = ''
} = {}) {
  return `${ANALYTICS_QUERY_CACHE_PREFIX}${companyUid}:${screenId}:${widgetId}:${stableStringify({
    filters,
    groupBy,
    revision
  })}`;
}

function readAnalyticsQueryCache(cacheKey, staleMs = ANALYTICS_QUERY_STALE_MS) {
  const memoryEntry = analyticsQueryMemoryCache.get(cacheKey);
  const now = Date.now();
  if (memoryEntry && now - memoryEntry.cachedAt <= staleMs) {
    return cloneJson(memoryEntry.value);
  }

  if (typeof window === 'undefined' || !window.sessionStorage) return null;
  try {
    const raw = window.sessionStorage.getItem(cacheKey);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (!entry || now - Number(entry.cachedAt || 0) > staleMs) return null;
    analyticsQueryMemoryCache.set(cacheKey, entry);
    return cloneJson(entry.value);
  } catch {
    return null;
  }
}

function writeAnalyticsQueryCache(cacheKey, value) {
  const entry = {
    cachedAt: Date.now(),
    value: cloneJson(value)
  };
  analyticsQueryMemoryCache.set(cacheKey, entry);

  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    window.sessionStorage.setItem(cacheKey, JSON.stringify(entry));
  } catch {
    // Session cache is a performance layer only.
  }
}

export function invalidateAnalyticsSessionCache(companyUid = '') {
  analyticsCacheInvalidationVersion += 1;
  const companyPrefix = companyUid ? `${ANALYTICS_QUERY_CACHE_PREFIX}${companyUid}:` : ANALYTICS_QUERY_CACHE_PREFIX;
  Array.from(analyticsQueryMemoryCache.keys()).forEach((key) => {
    if (!companyUid || key.startsWith(companyPrefix)) {
      analyticsQueryMemoryCache.delete(key);
    }
  });
  Array.from(analyticsQueryPendingRequests.keys()).forEach((key) => {
    if (!companyUid || key.startsWith(companyPrefix)) {
      analyticsQueryPendingRequests.delete(key);
    }
  });

  if (typeof window !== 'undefined' && window.sessionStorage) {
    try {
      const prefixes = [
        companyPrefix,
        companyUid ? `analytics-queried-widget-nodes-v1:${companyUid}:` : 'analytics-queried-widget-nodes-v1:',
        companyUid ? `analytics-widget-nodes-v1:${companyUid}:` : 'analytics-widget-nodes-v1:',
        companyUid ? `analytics-screen-filters-v1:${companyUid}:` : 'analytics-screen-filters-v1:'
      ];
      Object.keys(window.sessionStorage).forEach((key) => {
        if (prefixes.some((prefix) => key.startsWith(prefix))) {
          window.sessionStorage.removeItem(key);
        }
      });
    } catch {
      // Cache invalidation must not block operational writes.
    }

    try {
      window.dispatchEvent(new CustomEvent(ANALYTICS_SESSION_CACHE_INVALIDATED_EVENT, {
        detail: { companyUid: companyUid || '' }
      }));
    } catch {
      // Non-browser tests do not need the event.
    }
  }
}

async function getAuthHeader() {
  const user = auth.currentUser;
  if (!user) return {};
  try {
    const token = await user.getIdToken();
    return { Authorization: `Bearer ${token}` };
  } catch {
    return {};
  }
}

async function parseJsonSafe(response) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch (e) {
    return { raw: text };
  }
}

async function request(path, options = {}) {
  const response = await fetch(`${BACKEND_URL}${path}`, {
    ...options,
    headers: {
      ...jsonHeaders,
      ...(options.headers || {})
    }
  });
  const body = await parseJsonSafe(response);
  if (!response.ok) {
    const error = new Error(body?.detail || body?.error || `HTTP ${response.status}`);
    error.status = response.status;
    error.body = body;
    throw error;
  }
  return body;
}

function encodeQuery(params = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    if (Array.isArray(v)) {
      if (v.length) search.set(k, v.join(','));
      return;
    }
    search.set(k, String(v));
  });
  const s = search.toString();
  return s ? `?${s}` : '';
}

function getQuarterInfo(periodKey = '', reference = new Date()) {
  const requestedKey = String(periodKey || '').toUpperCase();
  let year = reference.getUTCFullYear();
  let quarter = Math.floor(reference.getUTCMonth() / 3) + 1;

  const match = requestedKey.match(/^(\d{4})-Q([1-4])$/);
  if (match) {
    year = Number(match[1]);
    quarter = Number(match[2]);
  }

  const startMonth = (quarter - 1) * 3;
  const startsAt = new Date(Date.UTC(year, startMonth, 1, 0, 0, 0));
  const endsAt = quarter === 4
    ? new Date(Date.UTC(year + 1, 0, 1, 0, 0, 0))
    : new Date(Date.UTC(year, startMonth + 3, 1, 0, 0, 0));
  const expiresAt = new Date(endsAt.getTime() - 1000);

  return {
    periodKey: `${year}-Q${quarter}`,
    periodLabel: `Q${quarter} ${year}`,
    startsAt: startsAt.toISOString(),
    endsAt: endsAt.toISOString(),
    expiresAt: expiresAt.toISOString()
  };
}

function normalizeTargetId(value, index = 0) {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return normalized || `department_${index + 1}`;
}

function normalizeHeadcountTargetPlan(payload = {}, updatedBy = 'frontend') {
  const quarter = getQuarterInfo(payload.periodKey || payload.period_key || '');
  const rows = Array.isArray(payload.departments)
    ? payload.departments
    : Object.values(payload.departmentTargets || {});

  const departments = rows
    .map((row, index) => {
      const label = String(row?.label || row?.department || row?.name || '').trim();
      if (!label) return null;
      const target = Math.max(0, Math.round(Number(row?.target ?? row?.headcount ?? row?.targetHires ?? 0) || 0));
      return {
        id: normalizeTargetId(row?.id || label, index),
        label,
        target
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.label.localeCompare(right.label));

  const nowIso = new Date().toISOString();
  const totalTarget = departments.reduce((sum, row) => sum + row.target, 0);

  return {
    schemaVersion: 2,
    ...quarter,
    departments,
    departmentTargets: Object.fromEntries(departments.map((row) => [row.id, row])),
    totalTarget,
    configured: totalTarget > 0,
    createdAt: payload.createdAt || nowIso,
    updatedAt: nowIso,
    updatedBy
  };
}

function emptyHeadcountTargetPlan(periodKey = '') {
  const quarter = getQuarterInfo(periodKey);
  return {
    schemaVersion: 2,
    ...quarter,
    departments: [],
    departmentTargets: {},
    totalTarget: 0,
    configured: false
  };
}

async function getHeadcountTargetsFromFirebase(companyUid, periodKey = '') {
  const quarter = getQuarterInfo(periodKey);
  const snapshot = await get(ref(database, `companies/${companyUid}/${HEADCOUNT_TARGET_SETTINGS_PATH}/${quarter.periodKey}`));
  const plan = snapshot.exists()
    ? normalizeHeadcountTargetPlan(snapshot.val() || {}, auth.currentUser?.uid || 'frontend')
    : emptyHeadcountTargetPlan(quarter.periodKey);
  return { companyUid, plan, source: 'firebase' };
}

async function saveHeadcountTargetsToFirebase(companyUid, plan = {}) {
  const updatedBy = auth.currentUser?.uid || auth.currentUser?.email || 'frontend';
  const normalized = normalizeHeadcountTargetPlan(plan, updatedBy);
  await set(
    ref(database, `companies/${companyUid}/${HEADCOUNT_TARGET_SETTINGS_PATH}/${normalized.periodKey}`),
    JSON.parse(JSON.stringify(normalized))
  );
  return { companyUid, plan: normalized, source: 'firebase' };
}

export const atsAnalyticsApi = {
  backendUrl: BACKEND_URL,

  /**
   * Report reads stay read-only by default. Backfill must be explicit so the UI can
   * distinguish data loading from historical repair actions.
   */
  async getReport({
    companyUid,
    dateRangeDays = 30,
    includeCompare = false,
    autoBackfill = false,
    jobIds = [],
    jobTitles = [],
    jobStatuses = ['published', 'closed', 'unpublished', 'pending_approval', 'draft', 'deleted'],
    includeArchivedCandidates = true,
    includeRemovedCandidates = true
  }) {
    return request(`/analytics/v2/report${encodeQuery({
      company_uid: companyUid,
      date_range_days: dateRangeDays,
      include_compare: includeCompare,
      auto_backfill: autoBackfill,
      job_ids: jobIds,
      job_titles: jobTitles,
      job_statuses: jobStatuses,
      include_archived_candidates: includeArchivedCandidates,
      include_removed_candidates: includeRemovedCandidates
    })}`, { method: 'GET' });
  },

  async getReportCache({
    companyUid,
    scopeType = 'report_center',
    templateId = '',
    screenId = '',
    dateRangeDays = 365,
    sectionIds = [],
    jobTitles = [],
    jobStatuses = []
  }) {
    const authHeader = await getAuthHeader();
    return request(`/analytics/v2/report-cache${encodeQuery({
      company_uid: companyUid,
      scope_type: scopeType,
      template_id: templateId,
      screen_id: screenId,
      date_range_days: dateRangeDays,
      section_ids: sectionIds,
      job_titles: jobTitles,
      job_statuses: jobStatuses
    })}`, {
      method: 'GET',
      headers: authHeader
    });
  },

  async recomputeReportCache(companyUid, options = {}) {
    const authHeader = await getAuthHeader();
    const response = await request('/analytics/v2/report-cache/recompute', {
      method: 'POST',
      headers: authHeader,
      body: JSON.stringify({
        company_uid: companyUid,
        date_range_days_values: options.dateRangeDaysValues || options.date_range_days_values || undefined,
        force: options.force !== false
      })
    });
    invalidateAnalyticsSessionCache(companyUid);
    return response;
  },

  async getInsights({
    companyUid,
    dateRangeDays = 30,
    includeCompare = false,
    jobIds = [],
    jobTitles = [],
    jobStatuses = ['published', 'closed', 'unpublished', 'pending_approval', 'draft', 'deleted'],
    includeArchivedCandidates = true,
    includeRemovedCandidates = true
  }) {
    return request(`/analytics/v2/insights${encodeQuery({
      company_uid: companyUid,
      date_range_days: dateRangeDays,
      include_compare: includeCompare,
      job_ids: jobIds,
      job_titles: jobTitles,
      job_statuses: jobStatuses,
      include_archived_candidates: includeArchivedCandidates,
      include_removed_candidates: includeRemovedCandidates
    })}`, { method: 'GET' });
  },

  async backfill(companyUid, options = {}) {
    return request(`/analytics/v2/backfill/${companyUid}`, {
      method: 'POST',
      body: JSON.stringify(options)
    });
  },

  async reconcile(companyUid, dateRangeDays = 30) {
    return request(`/analytics/v2/reconcile/${companyUid}${encodeQuery({ date_range_days: dateRangeDays })}`, {
      method: 'GET'
    });
  },

  async getTemplates(companyUid) {
    return request(`/analytics/v2/templates${encodeQuery({ company_uid: companyUid })}`, { method: 'GET' });
  },

  async saveTemplate(companyUid, template, templateId = null) {
    if (templateId) {
      return request(`/analytics/v2/templates/${templateId}`, {
        method: 'PATCH',
        body: JSON.stringify({ company_uid: companyUid, template, template_id: templateId })
      });
    }
    return request('/analytics/v2/templates', {
      method: 'POST',
      body: JSON.stringify({ company_uid: companyUid, template })
    });
  },

  async getUserViews(companyUid, userUid) {
    return request(`/analytics/v2/user-views${encodeQuery({ company_uid: companyUid, user_uid: userUid })}`, {
      method: 'GET'
    });
  },

  async saveUserView(companyUid, userUid, view, viewId = null) {
    return request('/analytics/v2/user-views', {
      method: 'POST',
      body: JSON.stringify({ company_uid: companyUid, user_uid: userUid, view, view_id: viewId })
    });
  },

  async getTargets(companyUid) {
    return request(`/analytics/v2/targets${encodeQuery({ company_uid: companyUid })}`, { method: 'GET' });
  },

  async saveTarget(companyUid, target, targetId = null) {
    const response = await request('/analytics/v2/targets', {
      method: 'POST',
      body: JSON.stringify({ company_uid: companyUid, target, target_id: targetId })
    });
    invalidateAnalyticsSessionCache(companyUid);
    return response;
  },

  async getAlerts(companyUid, dateRangeDays = 30, userUid = '') {
    return request(`/analytics/v2/alerts${encodeQuery({ company_uid: companyUid, date_range_days: dateRangeDays, user_uid: userUid })}`, {
      method: 'GET'
    });
  },

  async getJobs(companyUid, statuses = ['published', 'closed', 'unpublished', 'pending_approval', 'draft', 'deleted']) {
    return request(`/analytics/v2/jobs${encodeQuery({ company_uid: companyUid, statuses })}`, {
      method: 'GET'
    });
  },

  async getHealth(companyUid) {
    return request(`/analytics/v2/health${encodeQuery({ company_uid: companyUid })}`, {
      method: 'GET'
    });
  },

  async getHeadcountTargets(companyUid, periodKey = '') {
    const authHeader = await getAuthHeader();
    try {
      return await request(`/analytics/dashboard/headcount-targets${encodeQuery({ company_uid: companyUid, period_key: periodKey })}`, {
        method: 'GET',
        headers: authHeader
      });
    } catch (error) {
      if (error.status === 404) {
        return getHeadcountTargetsFromFirebase(companyUid, periodKey);
      }
      throw error;
    }
  },

  async saveHeadcountTargets(companyUid, plan = {}) {
    const authHeader = await getAuthHeader();
    try {
      const response = await request('/analytics/dashboard/headcount-targets', {
        method: 'POST',
        headers: authHeader,
        body: JSON.stringify({
          company_uid: companyUid,
          period_key: plan.periodKey || plan.period_key || '',
          departments: Array.isArray(plan.departments) ? plan.departments : []
        })
      });
      invalidateAnalyticsSessionCache(companyUid);
      return response;
    } catch (error) {
      if (error.status === 404) {
        const response = await saveHeadcountTargetsToFirebase(companyUid, plan);
        invalidateAnalyticsSessionCache(companyUid);
        return response;
      }
      throw error;
    }
  },

  async recomputeDashboardCompany(companyUid) {
    const response = await request('/analytics/dashboard/recompute/company', {
      method: 'POST',
      body: JSON.stringify({ company_uid: companyUid })
    });
    invalidateAnalyticsSessionCache(companyUid);
    return response;
  },

  async recomputeDashboardScreen(companyUid, screenId) {
    const response = await request('/analytics/dashboard/recompute/screen', {
      method: 'POST',
      body: JSON.stringify({ company_uid: companyUid, screen_id: screenId })
    });
    invalidateAnalyticsSessionCache(companyUid);
    return response;
  },

  async recomputeDashboardWidget(companyUid, screenId, widgetId) {
    const response = await request('/analytics/dashboard/recompute/widget', {
      method: 'POST',
      body: JSON.stringify({
        company_uid: companyUid,
        screen_id: screenId,
        widget_id: widgetId
      })
    });
    invalidateAnalyticsSessionCache(companyUid);
    return response;
  },

  async queryDashboardWidget(companyUid, screenId, widgetId, filters = {}, groupBy = '', options = {}) {
    const cacheKey = buildAnalyticsQueryCacheKey({
      companyUid,
      screenId,
      widgetId,
      filters,
      groupBy,
      revision: options.revision || ''
    });
    const staleMs = Number.isFinite(options.staleMs) ? options.staleMs : ANALYTICS_QUERY_STALE_MS;
    const forceRefresh = options.forceRefresh === true || options.bypassCache === true;

    if (!forceRefresh) {
      const cached = readAnalyticsQueryCache(cacheKey, staleMs);
      if (cached !== null) return cached;
      const pending = analyticsQueryPendingRequests.get(cacheKey);
      if (pending) return pending;
    }

    const requestInvalidationVersion = analyticsCacheInvalidationVersion;
    let queryPromise;
    queryPromise = (async () => {
      const authHeader = await getAuthHeader();
      return request('/analytics/dashboard/query/widget', {
        method: 'POST',
        headers: authHeader,
        body: JSON.stringify({
          company_uid: companyUid,
          screen_id: screenId,
          widget_id: widgetId,
          filters,
          group_by: groupBy
        })
      }).then((response) => {
        if (requestInvalidationVersion === analyticsCacheInvalidationVersion) {
          writeAnalyticsQueryCache(cacheKey, response);
        }
        return response;
      }).finally(() => {
        if (analyticsQueryPendingRequests.get(cacheKey) === queryPromise) {
          analyticsQueryPendingRequests.delete(cacheKey);
        }
      });
    })();
    analyticsQueryPendingRequests.set(cacheKey, queryPromise);
    return queryPromise;
  },

  async queryDashboardScreenFilters(companyUid, screenId) {
    const authHeader = await getAuthHeader();
    return request('/analytics/dashboard/query/screen-filters', {
      method: 'POST',
      headers: authHeader,
      body: JSON.stringify({
        company_uid: companyUid,
        screen_id: screenId
      })
    });
  },

  async requestAIBriefRefresh(companyUid, period) {
    const authHeader = await getAuthHeader();
    const response = await request('/analytics/dashboard/ai-brief/request-refresh', {
      method: 'POST',
      headers: authHeader,
      body: JSON.stringify({
        company_uid: companyUid,
        period
      })
    });
    invalidateAnalyticsSessionCache(companyUid);
    return response;
  },

  async getRecruiterActivity(companyUid, recruiterUid, periodDays = 30) {
    const authHeader = await getAuthHeader();
    return request('/analytics/recruiter/activity', {
      method: 'POST',
      headers: authHeader,
      body: JSON.stringify({
        company_uid: companyUid,
        recruiter_uid: recruiterUid,
        period_days: periodDays
      })
    });
  },

  async acknowledgeAlert(companyUid, alertId, userUid, note = '') {
    return request('/analytics/v2/alerts/acknowledge', {
      method: 'POST',
      body: JSON.stringify({ company_uid: companyUid, alert_id: alertId, user_uid: userUid, note })
    });
  },

  async generateAiNarrative({
    companyUid,
    dateRangeDays = 30,
    includeCompare = false,
    includeInsights = true,
    jobIds = [],
    jobTitles = [],
    jobStatuses = ['published', 'closed', 'unpublished', 'pending_approval', 'draft', 'deleted'],
    includeArchivedCandidates = true,
    includeRemovedCandidates = true
  }) {
    return request('/analytics/v2/ai-narrative', {
      method: 'POST',
      body: JSON.stringify({
        company_uid: companyUid,
        date_range_days: dateRangeDays,
        include_compare: includeCompare,
        include_insights: includeInsights,
        job_ids: jobIds,
        job_titles: jobTitles,
        job_statuses: jobStatuses,
        include_archived_candidates: includeArchivedCandidates,
        include_removed_candidates: includeRemovedCandidates
      })
    });
  },

  async getAiNarrative({
    companyUid,
    dateRangeDays = 30,
    includeCompare = false,
    includeInsights = true,
    jobIds = [],
    jobTitles = [],
    jobStatuses = ['published', 'closed', 'unpublished', 'pending_approval', 'draft', 'deleted'],
    includeArchivedCandidates = true,
    includeRemovedCandidates = true
  }) {
    return request(`/analytics/v2/ai-narrative${encodeQuery({
      company_uid: companyUid,
      date_range_days: dateRangeDays,
      include_compare: includeCompare,
      include_insights: includeInsights,
      job_ids: jobIds,
      job_titles: jobTitles,
      job_statuses: jobStatuses,
      include_archived_candidates: includeArchivedCandidates,
      include_removed_candidates: includeRemovedCandidates
    })}`, { method: 'GET' });
  },

  async trackEvent(payload, options = {}) {
    if (options.invalidate !== false && payload?.company_uid) {
      invalidateAnalyticsSessionCache(payload.company_uid);
    }
    return request('/analytics/v2/events', {
      method: 'POST',
      body: JSON.stringify({
        source_system: 'frontend_ats',
        provenance: 'observed',
        ...payload
      })
    });
  }
};

export async function emitAtsAnalyticsEvent(payload) {
  try {
    if (!payload?.company_uid || !payload?.event_type) return { skipped: true };
    invalidateAnalyticsSessionCache(payload.company_uid);
    return await atsAnalyticsApi.trackEvent(payload, { invalidate: false });
  } catch (error) {
    console.warn('ATS analytics event emit failed:', error?.message || error);
    return { skipped: false, failed: true, error: error?.message || String(error) };
  }
}

export function buildDefaultAnalyticsViewConfig(overrides = {}) {
  return {
    name: 'My Hiring Report Setup',
    filters: {
      dateRangeDays: 30,
      includeCompare: false,
      jobIds: [],
      jobTitles: [],
      jobStatuses: ['published', 'closed', 'unpublished', 'pending_approval', 'draft', 'deleted'],
      includeArchivedCandidates: true,
      includeRemovedCandidates: true
    },
    builder: {
      question: 'jobs-needing-attention',
      metric: 'stalledCandidates',
      groupBy: 'job',
      sortBy: 'stalledCandidates',
      chartType: 'table'
    },
    kpis: [
      'totalApplicants',
      'qualifiedRatePct',
      'hireRatePct',
      'timeToHireDays',
      'pipelineVelocityPerDay',
      'offerApprovalRatePct'
    ],
    charts: ['timeseries', 'jobPerformance', 'sourceBreakdown', 'funnel'],
    sections: ['summary', 'funnel', 'sources', 'insights', 'alerts', 'dataQuality'],
    exportTemplate: 'premium-default',
    comparePeriod: true,
    ...overrides
  };
}

export function getAnalyticsMetricLabel(metricKey) {
  const labels = {
    totalApplicants: 'Total Applicants',
    qualifiedRatePct: 'Qualified Rate',
    hireRatePct: 'Hire Rate',
    timeToHireDays: 'Time to Hire',
    timeInStageMedianDays: 'Median Time in Stage',
    pipelineVelocityPerDay: 'Pipeline Velocity',
    interviewCycleDays: 'Interview Cycle',
    offerApprovalRatePct: 'Offer Approval Rate',
    applicationToViewRatePct: 'Application-to-View Rate',
    applicationsPerHire: 'Applicants per Hire',
    interviewsPerHire: 'Interviews per Hire',
    activePipelineCandidates: 'Active Candidates',
    stalledCandidates: 'Stalled Candidates',
    stalledCandidatesPct: 'Stalled Candidate Rate',
    untouchedApplicants: 'Waiting for First Review',
    untouchedApplicantsPct: 'Waiting for First Review Rate',
    lateStageLosses: 'Late-Stage Losses',
    lateStageLossRatePct: 'Late-Stage Loss Rate',
    agingOpenJobs: 'Aging Open Roles'
  };
  return labels[metricKey] || metricKey;
}
